//*Import the React library
import React, { useState, useCallback, useEffect } from "react";

//*Import hooks from react-router-dom
import { useNavigate } from "react-router-dom";

//*Import the List component
import { List } from "../../../component/list/list";
import { Loading } from "../../../component/common/Loading";

//*Import the useTeachers hook
import { useTeachers } from "../../../hooks/teachers/useTeachers";

//*Import the AuthAPI service
import { AuthAPI } from "../../../services/apis/AuthAPI";

//*Import the UserRole enum
import { UserRole } from "../../../constants/enum/UserRole";

//* Import the CSVATeacherManagementView component
import { CSVTeacherManagementView } from "../../UsersTeacherView/list/CSVManagementView";
import { useMemo } from "react"; // Import useMemo from React

type Teacher = {
  id: string;
  name: string;
  email: string;
  is_auditor?: boolean;
  allow_select_all_questions?: boolean;
  createdAt: string;
  updatedAt: string;
};

type User = {
  role: UserRole;
};

var debounce = require("lodash.debounce");

export function UsersTeacherView() {
  const token = localStorage.getItem("token") || "";
  const navigate = useNavigate();

  const [me, setMe] = useState<User | null>(null);
  const [showWarning, setShowWarning] = useState(false);
  const [teacherToDelete, setTeacherToDelete] = useState<string | null>(null);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [showCSVTeacherModal, setShowCSVTeacherModal] = useState(false);
  const [totalTeachers, setTotalTeachers] = useState(0);

  const {
    teachers,
    loading,
    error,
    setSearch,
    pagination,
    setPagination,
    deleteTeacher,
    refetch,
    changePage,
    isDeleting,
  } = useTeachers(token);

  useEffect(() => {
    async function fetchMe() {
      try {
        const response = await AuthAPI.me(token);
        setMe(response.data);
      } catch (error) {
        console.error("Error fetching user details:", error);
      }
    }
    fetchMe();
  }, [token]);

  useEffect(() => {
    setTotalTeachers(pagination.totalItems || 0);
  }, [pagination.totalItems]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        if (value.trim().length >= 3) {
          setSearch(value);
        } else {
          setSearch("");
          setPagination((prev) => ({ ...prev, currentPage: 1 }));
        }
        setIsSearching(false);
      }, 500),
    [setSearch, setPagination]
  );

  // Cleanup the debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSearchInputChange = useCallback(
    (value: string) => {
      setSearchInputValue(value);
      setIsSearching(true);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (teacherToDelete) {
      try {
        const currentPageItems = teachers.length;
        const currentPage = pagination.currentPage;

        await deleteTeacher(teacherToDelete);

        if (currentPageItems === 1 && currentPage > 1) {
          changePage(currentPage - 1);
        }

        setShowWarning(false);
        setTeacherToDelete(null);
      } catch (error) {
        console.error("Error deleting Teacher:", error);
      }
    }
  }, [
    teacherToDelete,
    deleteTeacher,
    teachers.length,
    pagination.currentPage,
    changePage,
  ]);

  const handleDeleteActionClick = useCallback((id: string) => {
    setTeacherToDelete(id);
    setShowWarning(true);
  }, []);

  const handleChangePagination = useCallback(
    (value: number) => {
      changePage(value);
    },
    [changePage]
  );

  const onImportClick = () => {
    setShowCSVTeacherModal(true);
  };

  if (!me || isDeleting) {
    return <Loading />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="row">
      <List
        search_input_value={searchInputValue}
        hideManager
        showPermissions
        search_text="Pesquise pelo nome ou email do Professores"
        onChangeSearchInput={handleSearchInputChange}
        onSearchButtonClick={refetch}
        additionalButtons={
          me.role === UserRole.TEACHER_USER
            ? []
            : [
                {
                  label: "Importar Professores",
                  eventHandler: onImportClick,
                },
              ]
        }
        title={`Professores (Total: ${totalTeachers})`}
        not_found_message="Nenhum professor encontrado"
        button_link="/users-teacher/create"
        button_text="Adicionar Professores"
        deleting_message="Ao deletar este professor, ele não estará mais disponível no sistema"
        onDeleteCancel={() => setShowWarning(false)}
        onDeleteConfirm={handleDeleteConfirm}
        onDeleteActionClick={handleDeleteActionClick}
        onEditActionClick={(id) => navigate(`/users-teacher/edit/${id}`)}
        onPermissionActionClick={(id) =>
          navigate(`/users-teacher/permissions/${id}`)
        }
        onChangePagination={handleChangePagination}
        show_warning={showWarning}
        loading_screen={loading || isSearching}
        items={teachers.map((teacher: Teacher) => ({
          ...teacher,
          is_auditor: teacher.is_auditor ? "Sim" : "Não", // Transforma boolean em "Sim"/"Não"
          allow_select_all_questions: teacher.allow_select_all_questions
            ? "Sim"
            : "Não", // Transforma boolean em "Sim"/"Não"
          key: teacher.id,
        }))}
        pagination={{
          last_page: pagination.totalPages,
          current_page: pagination.currentPage,
        }}
        fields={[
          { name: "name", type: "text", header: "Nome" },
          { name: "email", type: "text", header: "Email" },
          { name: "createdAt", type: "date", header: "Criado em" },
          { name: "is_auditor", type: "text", header: "Auditor" },
          {
            name: "allow_select_all_questions",
            type: "text",
            header: "Selecionar todas as questões",
          },
        ]}
        loading_search={isSearching}
      />
      {showCSVTeacherModal && (
        <CSVTeacherManagementView
          token={token}
          onClose={() => setShowCSVTeacherModal(false)}
          refetch={refetch}
        />
      )}
    </div>
  );
}
// Remove the incorrect function definition below
/*
function useMemo(arg0: () => any, arg1: (React.Dispatch<React.SetStateAction<Pagination>> | ((searchTerm: string) => void))[]) {
  throw new Error("Function not implemented.");
}
*/

